/**
 * The seven questions — the whole frame of the delivery-side intelligence
 * surface, in one list. Ten in the design document, eleven by 2026-09-25,
 * seven since Tom approved the consolidation plan on 2026-09-27 (job #459), eight with
 * Campaigns (job #702, 2026-10-13):
 * Minutes, Courses, All use, Weak points, Leaving and Where and what retired
 * as pages and their content moved; the two never-built questions became
 * Content and Schools. Every retired address redirects, in the router.
 *
 * docs/delivery-side-intelligence-surface.md §1 names them and §3.1 makes them
 * checkable: one route per question, every one of them one tap from
 * the top bar. This file is the single place they are written down, so the top
 * bar, the router and the tests all read the same list and cannot drift.
 *
 * Array order is the top bar's order, and `n` follows it. This week leads
 * since job #328 (2026-10-13), and /intel lands on it.
 *
 * `built: false` means the page has not been built yet and the route renders the
 * honest not-yet card. That is a deliberate state, not a placeholder to be
 * quietly filled.
 */

/**
 * The scope a question is asked at — the ScopeRail's own vocabulary (job 340,
 * Tom 2026-09-12: "Yes to admin nav"). The top bar groups the questions by this, in
 * the order below, so the bar and the sidebar say the same words.
 */
export type QuestionGroup = 'Everyone' | 'One person'

export interface Question {
  /** 1-8, contiguous. Renumbered in bar order on 2026-09-27 when eleven pages became seven (job #459), and again when Campaigns came in third (job #702). */
  n: number
  /** Route segment under /intel. */
  slug: string
  /** Short name in the top bar. */
  tab: string
  /** The question a person would say out loud. */
  question: string
  group: QuestionGroup
  built: boolean
}

export const QUESTIONS: readonly Question[] = [
  {
    // THE PULSE (job #328, Tom 2026-10-13: "less data - more obviously").
    // What people are DOING, last 7 days rolling against the 7 before. It
    // leads the bar and /intel lands on it; it took over from Business as the
    // front door. A finding with no page of its own lands here too.
    n: 1,
    slug: 'this-week',
    tab: 'This week',
    question: 'What are people doing this week?',
    group: 'Everyone',
    built: true,
  },
  {
    // THE BUSINESS NUMBERS (job #585, Tom 2026-09-21 20:07Z: "not just
    // intelligence for insights and course quality, but the typical numbers a
    // business wants"). It led the bar until job #328 put This week first. Since the
    // consolidation of 2026-09-27 (job #459) it also carries who is about to
    // leave and the one line of where people are and on what.
    n: 2,
    slug: 'business',
    tab: 'Business',
    question: 'What are the business numbers — how many accounts, how many active, how many paying and on what, and who has lapsed?',
    group: 'Everyone',
    built: true,
  },
  {
    // CAMPAIGNS (job #702, Tom 2026-10-13 via Watson: make campaigns such as the India Meta
    // ads through IME visible). Per campaign link, arrivals to course picked to first lesson to
    // sign-up to paid, by country and by day. Read from a kept answer, refreshed on demand.
    n: 3,
    slug: 'campaigns',
    tab: 'Campaigns',
    question: 'Which campaign links bring people in, and how far do they get — a course, a lesson, a sign-up, a payment?',
    group: 'Everyone',
    built: true,
  },
  {
    // THE LEARNING PAGE (job #456, Tom 2026-09-27: "what can we learn about use BEHAVIOUR that
    // can help us identify leading indicators that are most predictive of future behaviour ...
    // which courses are more popular, more difficult, create the most wow moments, get the most
    // feedback"). Read from the nightly snapshot, not computed on open. It took over Minutes
    // and Courses when they retired as pages.
    n: 4,
    slug: 'learning',
    tab: 'Learning',
    question: 'Which early behaviours tell us who will keep learning, and which courses are popular, hard and loved?',
    group: 'Everyone',
    built: true,
  },
  {
    // Weak points and the never-built Losing people, as one page (job #459).
    n: 5,
    slug: 'content',
    tab: 'Content',
    question: 'Where does a course hurt people?',
    group: 'Everyone',
    built: true,
  },
  {
    // The schools and organisations thirds of All use, plus the old
    // question 10 about trials and going dark, finally built (job #459).
    // The individuals list rides along at the bottom so nothing All use
    // showed is lost.
    n: 6,
    slug: 'schools',
    tab: 'Schools',
    question: 'Which schools and organisations are alive, which trials end soon, which have gone quiet?',
    group: 'Everyone',
    built: true,
  },
  {
    // Stays live: it answers "did my fix land", which cannot wait for a night.
    n: 7,
    slug: 'working',
    tab: 'Working now',
    question: 'Is the app working right now, and did my last fix land?',
    group: 'Everyone',
    built: true,
  },
  {
    n: 8,
    slug: 'person',
    tab: 'One person',
    question: "What is this one person's story, and what has gone wrong for them?",
    group: 'One person',
    built: true,
  },
] as const

export const QUESTION_GROUPS: readonly QuestionGroup[] = ['Everyone', 'One person'] as const

export function questionPath(q: Question): string {
  return `/intel/${q.slug}`
}

export function questionBySlug(slug: string): Question | undefined {
  return QUESTIONS.find((q) => q.slug === slug)
}

/**
 * The pages that retired on 2026-09-27 (job #459), each with the page its
 * content moved to. The router turns every one into a redirect, query kept,
 * so a bookmark or a pasted link still lands on its answer.
 */
export const RETIRED_QUESTIONS: Readonly<Record<string, Question['slug']>> = {
  pulse: 'learning',
  courses: 'learning',
  'all-use': 'schools',
  'weak-points': 'content',
  'losing-people': 'content',
  leaving: 'business',
  'where-and-what': 'business',
  organisations: 'schools',
}
